import React, {useCallback, useEffect} from 'react';
import PropTypes from 'prop-types';

import {Section} from "controls/react/layout/section";
import {FormEntry} from "controls/react/form/formEntry";
import {AntInput, AntTextArea} from "controls/react/ant/antInput";
import {useStateLink} from "core/react/links";
import {Toolbar} from 'controls/react/layout/toolbar'
import {AntCheckbox} from "controls/react/ant/antCheckbox";
import {TagsSelect} from "controls/react/tagsSelect";
import {AntSelect} from "controls/react/ant/ant-select/antSelect";
import {AntInputNumber} from "controls/react/ant/antInputNumber";
import {IconButton} from "controls/react/form/iconButton";
import {Window} from "controls/react/kendoWrappers/window";
import {ActionButtons} from "controls/react/form/actionButtons";
import {topLeftCornerPosition} from 'controls/modalWindow';
import CustomNotification from 'controls/customNotification';
import {LayoutType, getLayoutTypeDataSource} from "controls/designer/features/adaptiveLayout/layoutType"
import {PresentationApi} from "areas/dashboards/api";
import {AdaptiveAlgorithm, getAdaptiveAlgorithmDataSource} from "controls/designer/features/adaptiveLayout/adaptiveAlgorithm";
import {translator} from "core/localization";
import {EditOutlined, QuestionCircleOutlined} from "@ant-design/icons";
import {AntButton} from "controls/react/ant/antButton";
import {openTreeListPopup} from "areas/dashboards/pages/DashboardsPage/components/DashboardsTree/treeListPopup";
import {HelpIcon} from "controls/react/form/formEntryNew";
import { AntModal, ModalPosition } from 'controls/react/ant/antModal';

const i18n = translator({
  "Lock to screen": {
    "no": "Fest til skjerm",
    "en": "Lock to screen"
  },
  "Slide": {
    "no": "Skyv"
  },
  "Off": {
    "no": "Av"
  },
  "Visibility Settings": {
    "no": "Innstillinger for synlighet",
    "en": "Visibility settings"
  },
  "Position": {
    "no": "Posisjon"
  },
  "Information Options": {
    "no": "Informasjonsinnstillinger",
    "en": "Information options"
  },
  "Show name": {
    "no": "Vis navn",
    "en": "Show name"
  },
  "Show event time": {
    "no": "Vis oppdateringsstid",
    "en": "Show eventtime"
  },
  "Widget Default Settings": {
    "no": "Widget standard innstillinger",
    "en": "Widget default settings"
  },
  "Width (px)": {
    "no": "Bredde (px)"
  },
  "Height (px)": {
    "no": "Høyde (px)"
  },
  "Show headers": {
    "no": "Vis overskrift",
    "en": "Show header"
  },
  "Mode": {
    "no": "Modus",
    "en": "Mode"
  },
  "Public mode tooltip": {
    "en": "Public mode: Gives access to dashboard with dashboard URL without credentials.\nPrivate (unique session id): Gives access to dashboard with dashboard URL with credentials. Session id is unique and will not be reused in other ceeview instances within the browser. \nPrivate (reuse session id): Gives access to dashboard with dashboard URL with credentials. Session id is browser wide and will be reused in other ceeview sessions within the browser",
    "no": "Offentlig modus: Gir tilgang til dashbord med dashboard-URL uten innlogging.\nPrivat (unik sesjons id): Gir tilgang til dashboard med dashboard-URL med innlogging. Sesjons-ID er unik og vil ikke bli gjenbrukt i andre ceeview instanser i nettleseren. \nPrivat (gjenbruk sesjons-ID): Gir tilgang til dashbord med dashbord-URL med innlogging. Sesjons-ID er nettleseromfattende og vil bli gjenbrukt i andre ceeview instanser i nettleseren"
  },
  "Dashboard info": {
    "en": "Dashboard replace our serviceboards as a presentation tool. It provides the same widgets, in addition to the shapes our presentation component gave. It gives greater flexibility in terms of design and layout.",
    "no": "Dashboard erstatter tjenestetavle og presentasjon som presentasjonsverktøy. Det gir de samme widgetene som tjenestetavler, i tillegg til figurene som presentasjonskomponenten ga. Det gir større fleksibilitet når det gjelder design og utforming."
  },
  "Allow redirect in public/private mode": {
    "no": "Tillatt omdirigering i public/privat visning",
    "en": "Allow redirect in public/private mode"
  },
  "Allow redirect tooltip": {
    "en": "Login is required in public mode",
    "no": "Det kreves login i offentlig visning"
  },
  "Name exists": {
    "en": "An identical name exist in this account, please use another name.",
    "no": "Et identisk navn finnes på denne kontoen, bruk et annet navn."
  },
  "Show update time": {
    "no": "Vis oppdateringstid",
    "en": "Show update time"
  },
	"Algorithm tooltip": {
		"en": "Flow will based on available vertical resolution fit the widgets to screen\nNumber Ordering will fit the widget based on value (from low to high) position the widget in available resolution\nSet value in the Widget/Shape property Arrange tab, field Layout Index.",
		"no": "Flyt bruker tilgjengelig vertikal oppløsning for å tilpasse widgetene til skjermen\nNummer rekkefølge plasserer widget basert på tallverdi(lav til høy) i tilgjengelig oppløsning\nAngi verdi i Widget/Shape Egenskaper, Arrangere fane, feltet Rekkefølge."
	},
	"Layout tooltip": {
		"en": "Scrollable: Adds scrollbars if content exceeds the screen resolution.\nScale: Scales content to fit the screen resolution.\nResponsive: Adjusts layout to fit the resolution by shifting widgets vertically based on available width.",
		"no": "Skroll: Legger til skrollfelt hvis innholdet overskrider skjermoppløsningen.\nSkaler: Skalerer innhold for å passe til skjermoppløsningen.\nResponsiv: Justerer oppsettet for å passe oppløsningen ved å flytte widgets vertikalt basert på tilgjengelig bredde."
	},
  "Layout Algorithm": {
    "no": "Oppsett algoritme",
    "en": "Layout algorithm"
  },
  "Dashboard property": {
    "no": "Dashboard innstillinger"
  },
  "Public (no login required)": {
    "no": "Offentlig (krever ikke innlogging)"
  },
  "Private (Shared session)": {
    "no": "Privat (delt sesjon)"
  },
  "Private (Unique session)": {
    "no": "Privat (unik sesjon)"
  },
	'Version': {
	  no: 'Versjon',
	},
	'Disable editing': {
	  no: 'Deaktiver redigering',
	},
	'This dashboard is read-only by Ceeview. Enabling local edits may prevent it from receiving future updates.': {
	  no: 'Dette dashbordet er skrivebeskyttet av Ceeview. Aktivering av redigering kan hindre fremtidige oppdateringer av dashbordet.'
	},
	'Major version is manual. Minor version updates automatically on save. Clear this field to disable versioning.': {
	  no: 'Hovedversjon (Major) må endres manuelt. Underversjon (Minor) oppdateres automatisk ved hver lagring. Tøm feltet for å skjule versjonsnummeret'
	},
	'Path tooltip': {
	  en: 'Path: Organize the dashboard by placing it in a folder within the tree list. This allows you to filter the dashboard grid based on its location.',
	  no: 'Sti: Organiser dashbordet ved å plassere det i en mappe i trelisten. Dette kan brukes til å filtrere oversikten i dashbord-tabellen.'
	},
	'Edit': {
	  no: 'Rediger',
	}
});


export const PropertiesForm = props => {
	let modalNotification = React.useRef(new CustomNotification({
		appendToElement: '#popup',
		status: 'success',
		type: 'icon',
		hideOnClick: true
	}));

	let isMobileView = props.designer.store.viewManager.view == 'mobile'

	const propertiesLink = useStateLink(props.properties || {});

	propertiesLink.get('layoutType')
		.changing(({rootStore, value, e}) => {
			if(!rootStore.adaptiveAlgorithm )
			{
				rootStore.adaptiveAlgorithm = AdaptiveAlgorithm.Flow
			}

			if(props.properties.id)
				return;

			if(value == LayoutType.Adaptive && rootStore.widgetWidth == 400 && rootStore.widgetHeight == 320){
				rootStore.widgetWidth = 360;
				rootStore.widgetHeight = 360;
			}else if(rootStore.widgetWidth == 360 && rootStore.widgetHeight == 360){
				rootStore.widgetWidth = 400;
				rootStore.widgetHeight = 320;
			}
		});

	const nameLink = propertiesLink.get('tag')
		.required();

	const pathLink = propertiesLink.get('path');
	const versionLink = propertiesLink.get('version')
		.changing(({rootStore, value, initialValue, e}) => {
			if (value && !value.match(/^[0-9]+(\.[0-9]*)?$/)) {
				rootStore.version = propertiesLink.get('version').value;
			}
		});

	useEffect(() => {
		if (!versionLink.value) {
			versionLink.update('1.0');
		}
	}, []);

	const onVersionChanged = useCallback((...args) => {
		const value = propertiesLink.get('version').value;
		if (value && !value.match(/^[0-9]+\.[0-9]+$/)) {
			propertiesLink.get('version').update(value + '.0');
		}
	}, [versionLink])

	const onOk = React.useCallback(async () => {
		if (nameLink.invalid){
			modalNotification.current.setOptions({
				message: i18n('Please complete all required fields.'),
				status: 'error'
			}).show();
			return;
		}

		let name = propertiesLink.value.tag;
		let nameExists = await Utils.checkIfNameExists(PresentationApi.getSearchUrl(), name, modalNotification.current, true, props.properties?.tag);
		if(nameExists) {
			return;
		}
		props.onSave(propertiesLink.value);
	}, [props.onSave]);

	const titleIcon = {
		class: 'question-sign',
		tooltip: i18n('Dashboard info')
	}

	const canSave = React.useCallback(() => {
		if(propertiesLink.get('tag').value?.trim() !== '') {
			return true;
		}
		return false;
	}, [])

	const onPublicModeChange = React.useCallback((value) => {
		if(value == 'public'){
			propertiesLink.update((v) => {
				v.isPublic = true
				v.sharedSession = false
			})
		}else if(value == 'private'){
			propertiesLink.update((v) => {
				v.isPublic = false
				v.sharedSession = false
			})
		}else{
			propertiesLink.update((v) => {
				v.isPublic = false
				v.sharedSession = true
			})
		}
	}, [])

	const publicModeValue = propertiesLink.get('isPublic').value == true
		? 'public'
		: (propertiesLink.get('sharedSession').value == true
			? 'shared'
			: 'private')

	const selectPath = React.useCallback(() => {
		openTreeListPopup({
			onOk: (path) => {
				pathLink.update(path);
			}
		});
	}, [pathLink])

	return <AntModal title={i18n('Dashboard property')}
	                 width={900}
	                 positionType={ModalPosition.Mouse}
	                 footer={null}
	                 onCancel={props.onCancel}>
			<Section appearance={"none"}>
				<Section layout={"equal"} direction={"row"}>
					<Section appearance={"frame"} title={i18n('Configuration')} childrenPadding={true}>
						<div id="popup"></div>
						<FormEntry valueLink={nameLink} label={i18n('Name')}>
							<AntInput placeholder={i18n('Please input Name...')}/>
						</FormEntry>
						<FormEntry label={i18n('Description')} vertical={true}>
							<AntTextArea valueLink={propertiesLink.get('description')}/>
						</FormEntry>
						<FormEntry valueLink={pathLink} label={i18n('Path')}>
							<IconButton iconName={"question-sign"}
							            title={i18n('Path tooltip')}/>
							<AntInput readOnly={true} />
							<AntButton icon={<EditOutlined />} onClick={selectPath} title={i18n('Edit')} />
						</FormEntry>
						<FormEntry label={i18n('Tags')} vertical={true}>
							<TagsSelect {...propertiesLink.get('tags').props}/>
						</FormEntry>
						{!isMobileView &&
							<FormEntry label={i18n('Layout')} title={i18n('Layout tooltip')}>
								<IconButton iconName={"question-sign"}
								            title={i18n('Layout tooltip')}/>
								<AntSelect options={getLayoutTypeDataSource()}
								           {...propertiesLink.get('layoutType').props} />
							</FormEntry>
						}
						{!isMobileView && propertiesLink.get('layoutType').value == LayoutType.Adaptive &&
							<FormEntry label={i18n('Layout Algorithm')} title={i18n('Algorithm tooltip')}>
								<IconButton iconName={"question-sign"}
								            title={i18n('Algorithm tooltip')}/>
								<AntSelect options={getAdaptiveAlgorithmDataSource()}
								           {...propertiesLink.get('adaptiveAlgorithm').props} />
							</FormEntry>
						}

						<FormEntry title={i18n('Allow redirect tooltip')}>
							<AntCheckbox
								valueLink={propertiesLink.get('allowRedirects')}>{i18n('Allow redirect in public/private mode')}</AntCheckbox>
						</FormEntry>
						<FormEntry valueLink={versionLink} label={i18n('Version')}>
							<IconButton iconName={"question-sign"}
							            title={i18n('Major version is manual. Minor version updates automatically on save. Clear this field to disable versioning.')}/>
							<AntInput onBlur={onVersionChanged} />
						</FormEntry>
						<FormEntry>
							<AntCheckbox
								valueLink={propertiesLink.get('editingDisabled')}>{i18n('Disable editing')}&nbsp;&nbsp;<HelpIcon tooltip={i18n('This dashboard is read-only by Ceeview. Enabling local edits may prevent it from receiving future updates.')} /></AntCheckbox>
						</FormEntry>
					</Section>
					<Section appearance={"none"}>
						<Section appearance={"frame"} title={i18n('Visibility Settings')} childrenPadding={true}>
							<FormEntry label={i18n('Mode')}>
								<IconButton iconName={"question-sign"}
								            title={i18n('Public mode tooltip')}/>
								<AntSelect options={visibilityDataList}
								           value={publicModeValue}
								           onChange={onPublicModeChange}/>
							</FormEntry>
							<FormEntry label={i18n('Information Options')}>
								<AntSelect options={informationDataList}
										   {...propertiesLink.get('informationOption').props}/>
							</FormEntry>
							<FormEntry label={i18n('Position')}>
								<AntSelect options={positionDataList} {...propertiesLink.get('position').props}/>
							</FormEntry>
							<FormEntry>
								<AntCheckbox valueLink={propertiesLink.get('showName')}>{i18n('Show name')}</AntCheckbox>
							</FormEntry>
							<FormEntry>
								<AntCheckbox
									valueLink={propertiesLink.get('showUpdateTime')}>{i18n('Show update time')}</AntCheckbox>
							</FormEntry>
						</Section>
						<Section appearance={"frame"} title={i18n('Widget Default Settings')} childrenPadding={true}>
							<FormEntry>
								<AntCheckbox valueLink={propertiesLink.get('showWidgetHeader')}>{i18n('Show headers')}</AntCheckbox>
							</FormEntry>
							<FormEntry label={i18n('Width (px)')}>
								<AntInputNumber valueLink={propertiesLink.get('widgetWidth')}/>
							</FormEntry>
							<FormEntry label={i18n('Height (px)')}>
								<AntInputNumber valueLink={propertiesLink.get('widgetHeight')}/>
							</FormEntry>
						</Section>
					</Section>
				</Section>

				<Toolbar>
					<ActionButtons onSave={onOk}
					               onCancel={props.onCancel}
					               update={props.properties.id != null}
								   saveDisabled={!canSave()}/>
				</Toolbar>

			</Section>

		</AntModal>
}

PropertiesForm.propTypes = {
	onSave: PropTypes.func.isRequired,
	onCancel: PropTypes.func.isRequired,
}

const visibilityDataList = [{
	label: i18n('Public (no login required)'),
	value: 'public'
},{
	label: i18n('Private (Unique session)'),
	value: 'private'
},{
	label: i18n('Private (Shared session)'),
	value: 'shared'
}];

const informationDataList = [{
	label: i18n('Lock to screen'),
	value: 'locked'
},{
	label: i18n('Slide'),
	value: 'slide'
},{
	label: i18n('Off'),
	value: 'off'
}];

const positionDataList = [{
	label: i18n('Left'),
	value: 'left'
},{
	label: i18n('Right'),
	value: 'right'
},{
	label: i18n('Center'),
	value: 'center'
}];
